<template>
	<div
		v-if="formData.rewardItem && formData.rewardItem.length > 0 && formData.status === DAPP_REWARD_STATUS.DEFAULT"
		class="container py-3"
	>
		<PromotionDappRewardItem
			:thumbnail="formData.rewardItem[0].thumbnail"
			:name="formData.rewardItem[0].name"
			:type="formData.type.value"
			:discount-type="getDiscountType(formData.rewardItem[0])"
			:discount="formData.rewardItem[0].stockOption.params.discount"
			:is-stackable="formData.isStackable"
			@onEdit="handleEdit"
			@onRemove="handleRemove"
		/>
	</div>
	<div v-else class="container">
		<CRow class="my-3 align-items-center">
			<CCol md="11">
				<h3 class="typo-h5 position-relative mb-0">
					{{ isEdit ? 'Edit Reward' : 'Create Reward' }}
				</h3>
			</CCol>
			<CCol md="1">
				<CButton
					class="btn-delete-image"
					@click="handleRemove"
				>
					<CIcon
						class="icon-trash"
						name="cil-trash"
					/>
				</CButton>
			</CCol>
		</CRow>

		<hr class="line">

		<CRow>
			<CCol md="12">
				<h3 class="typo-h5 position-relative">
					Apply Reward
				</h3>
			</CCol>

			<CCol md="12" class="mt-3">
				<BaseDropDown
					v-model="formData.type"
					:searchable="false"
					:options="DAPP_REWARD_TYPE_OPTION"
					:allow-empty="false"
					class="select-custom"
					label="name"
					track-by="value"
					label-drop-down="Reward Type"
					@input="handleSelectRewardType"
				/>
			</CCol>

			<CCol md="12">
				<div class="p-3 typo-body-2 example">
					<span class="font-weight-bold">Example: </span>
					<span v-if="isFreebie">Buy MacBook Pro M3 Pro → Get Y qty. Freebie</span>
					<span v-if="isITec">Buy MacBook Pro M3 Pro → Get Discount Magic Mouse applied ( based on iTEC Min price)</span>
					<span v-else>Buy MacBook Pro M3 Pro → 20% Off Magic Mouse / ฿100 off Magic Mouse</span>
				</div>
			</CCol>

			<CCol md="12">
				<CRow class="py-3 mt-3">
					<CCol>
						<h6 class="mb-0">
							Combine with other discounts (Stackable)
						</h6>
						<div
							class="typo-caption color-black-45"
							data-test-id="description"
						>
							*Can be combined with other discounts in order
						</div>
					</CCol>

					<CCol md="3">
						<div class="form-group-switch d-flex justify-content-end">
							<CSwitch
								:checked.sync="formData.isStackable"
								variant="3d"
								size="sm"
								:disabled="isFreebie"
								class="switch-custom"
								color="success"
							/>
							<span class="typo-body-2" :class="{ 'text-disabled': isFreebie }">
								{{ formData.isStackable ? 'On' : 'Off' }}
							</span>
						</div>
					</CCol>
				</CRow>
			</CCol>

			<CCol
				v-if="isFreebie"
				md="12"
			>
				<PromotionDiscountFreebie
					:key="`${formData.type.value}-${formData.uuid}`"
					ref="promotionDiscountFreebieRef"
					class="freebie-box"
					:products="formData.rewardItem"
					:reserve-skus="reserveSkus"
					is-dapp-bundle
					reward-qty-header="Reward qty."
					@update:products="handleItemListChange"
				/>
				<div
					v-if="$v.formData.rewardItem.$dirty && $v.formData.rewardItem.$invalid"
					class="danger mb-2"
				>
					{{ $t('global.error.required') }}
				</div>
			</CCol>

			<CCol v-else md="12">
				<PromotionDappRewardDiscount
					:key="`${formData.type.value}-${formData.uuid}`"
					ref="promotionDappRewardListRef"
					:reward-type="formData.type.value"
					:items="formData.rewardItem"
					:reserve-skus="reserveSkus"
					@update:products="handleItemListChange"
				/>
			</CCol>
		</CRow>

		<hr class="line mt-0 mb-0">

		<CRow class="condition-container">
			<!-- Freebie reward limit -->
			<CCol v-if="isFreebie" md="12">
				<PromotionDappFreebieLimitPerOrder
					ref="promotionDappFreebieLimitPerOrder"
					:form-data="formData"
				/>
			</CCol>

			<CCol v-if="isFreebie" md="12">
				<hr class="line ml-0 mr-0 mb-0 mt-0">
			</CCol>

			<CCol md="12">
				<PromotionDappLimitAll
					ref="promotionDappLimitAll"
					:form-data="formData"
				/>
			</CCol>
		</CRow>

		<hr class="line mt-0 mb-0">

		<CRow class="py-3 align-items-center">
			<CCol
				md="8"
				class="d-flex align-items-center"
			>
				<CButton
					class="btn-delete-image pl-0"
					@click="handleRemove"
				>
					<CIcon
						class="icon-trash"
						name="cil-trash"
					/>
					<span class="ml-1">Remove</span>
				</CButton>
			</CCol>
			<CCol md="2">
				<CButton
					color="tertiary"
					class="modal-button transparent mr-3"
					@click="handleCancel"
				>
					Cancel
				</CButton>
			</CCol>
			<CCol md="2">
				<CButton
					color="primary"
					class="modal-button transparent"
					@click="handleSave"
				>
					{{ isEdit ? 'Edit' : 'Save' }}
				</CButton>
			</CCol>
		</CRow>
	</div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

import PromotionDappRewardItem from '@/components/PromotionDappRewardItem.vue';
import PromotionDiscountFreebie from '@/components/PromotionDiscountFreebie.vue';
import PromotionDappRewardDiscount from '@/components/PromotionDappRewardDiscount.vue';
import PromotionDappFreebieLimitPerOrder from '@/components/PromotionDappFreebieLimitPerOrder.vue';
import PromotionDappLimitAll from '@/components/PromotionDappLimitAll.vue';

import {
	DISCOUNT_TYPE_OPTIONS,
	DAPP_REWARD_TYPE_OPTION,
	DAPP_REWARD_TYPE,
	DAPP_REWARD_FREEBIE_CONDITION_EVERY,
	DAPP_REWARD_DISCOUNT_CONDITION_EVERY,
	DAPP_REWARD_FREEBIE_CONDITION_EVERY_OPTION,
	DAPP_REWARD_DISCOUNT_CONDITION_EVERY_OPTION,
	DAPP_REWARD_STATUS,
} from '../enums/promotions';

export default {
	name: 'PromotionDappStepRewardForm',

	components: {
		PromotionDappRewardItem,
		PromotionDiscountFreebie,
		PromotionDappRewardDiscount,
		PromotionDappFreebieLimitPerOrder,
		PromotionDappLimitAll,
	},

	props: {
		uid: {
			type: String,
			default: '',
		},
		reward: {
			type: Object,
			default: null,
		},
		reserveSkus: {
			type: Array,
			default: () => [],
		},
	},

	validations() {
		return {
			formData: {
				type: {
					required,
				},
				rewardItem: {
					required,
				},
			},
		};
	},

	data() {
		const formData = this.reward;
		if (!this.reward.conditionType) {
			formData.conditionType = this.reward.type.value === DAPP_REWARD_TYPE.FREEBIE
				? DAPP_REWARD_FREEBIE_CONDITION_EVERY.QUANTITY
				: DAPP_REWARD_DISCOUNT_CONDITION_EVERY.ORDER;
		}
		if (formData.type.value === DAPP_REWARD_TYPE.FREEBIE) {
			formData.isStackable = false;
		}

		const selectedCondition = this.reward.type.value === DAPP_REWARD_TYPE.FREEBIE
			? DAPP_REWARD_FREEBIE_CONDITION_EVERY_OPTION
				.find((option) => option.value === formData.conditionType)
			: DAPP_REWARD_DISCOUNT_CONDITION_EVERY_OPTION
				.find((option) => option.value === formData.conditionType);
		return {
			DAPP_REWARD_TYPE,
			DISCOUNT_TYPE_OPTIONS,
			DAPP_REWARD_TYPE_OPTION,
			DAPP_REWARD_FREEBIE_CONDITION_EVERY_OPTION,
			DAPP_REWARD_DISCOUNT_CONDITION_EVERY_OPTION,
			DAPP_REWARD_STATUS,
			selectedCondition,
			formData,
		};
	},

	computed: {
		isPrice() {
			return this.selectedCondition.value === DAPP_REWARD_FREEBIE_CONDITION_EVERY.PRICE;
		},
		isFreebie() {
			return this.formData.type.value === DAPP_REWARD_TYPE.FREEBIE;
		},
		isITec() {
			return this.formData.type.value === DAPP_REWARD_TYPE.ITEC;
		},
		isEdit() {
			return this.formData.status === DAPP_REWARD_STATUS.EDIT;
		},
	},

	methods: {
		getDiscountType(item) {
			return item?.stockOption?.discountType;
		},
		handleSelectRewardType(type) {
			this.formData.type = type;
			this.formData.rewardItem = [];
			if (this.isFreebie) {
				this.formData.isStackable = false;
			}
		},
		handleItemListChange(products) {
			this.formData.rewardItem = products;
		},
		handleEdit() {
			this.formData.status = DAPP_REWARD_STATUS.EDIT;
			this.$emit('onChangeStatus', this.formData);
		},
		handleRemove() {
			if (this.uid == null) {
				return;
			}
			this.$emit('onRemove', this.uid);
		},
		handleCancel() {
			this.formData.status = DAPP_REWARD_STATUS.DEFAULT;
			this.$emit('onCancel', this.formData);
		},
		handleSave() {
			const isDiscountValueInvalid = this.isFreebie
				? false
				: this.$refs.promotionDappRewardListRef.handleDiscountValueTouch();
			const promotionDiscountFreebieInvalid = this.isFreebie
				? this.$refs.promotionDiscountFreebieRef.handleTouch()
				: false;
			const vPromotionDappFreebieLimitPerOrder = this.isFreebie
				? this.$refs.promotionDappFreebieLimitPerOrder.$v
				: null;
			const vPromotionDappLimitAll = this.$refs.promotionDappLimitAll.$v;

			this.$v.$touch();
			if (vPromotionDappFreebieLimitPerOrder) {
				vPromotionDappFreebieLimitPerOrder.$touch();
			}

			vPromotionDappLimitAll.$touch();

			if (this.$v.$invalid
				|| isDiscountValueInvalid
				|| promotionDiscountFreebieInvalid
				|| (this.isFreebie && vPromotionDappFreebieLimitPerOrder.$invalid)
				|| vPromotionDappLimitAll.$invalid
			) {
				return;
			}

			if (this.isFreebie) {
				this.formData.isStackable = false;
			}

			this.formData.status = DAPP_REWARD_STATUS.DEFAULT;
			this.$emit('update:reward', this.formData);
		},
	},
};
</script>

<style lang="scss" scoped>
.container {
	padding: 0 16px;
	border: 1px solid #ddd;
	border-radius: 4px;

	.condition-container {
		background-color: #fafafa;
	}
}

.line {
	margin-left: -16px;
	margin-right: -16px;
}

.freebie-box {
	margin-bottom: 0 !important;
}

.freebie-apply {
	gap: 8px;
}

.example {
	background-color: #459d4314;
	border-radius: 4px;
}

.text-disabled {
	color: #ddd !important;
}

::v-deep .switch-custom .c-switch-input:disabled ~ .c-switch-slider {
	background-color: #ddd;
	border-color: #ddd;
	opacity: 1;

	&::before {
		background-color: #fff;
		border-color: #ddd;
	}
}
</style>
